export type LearningReviewContent = {
  text: string;
  mediaUrl?: string;
};

export type LearningReviewQuestion = {
  prompt: string;
  promptMediaUrl?: string;
  choices: Array<LearningReviewContent & { correct: boolean }>;
};

export type LearningReviewAttempt = {
  label: string;
  questionIndex: number;
  wrongChoiceIndices?: number[];
  wrongCount?: number;
  timeoutCount?: number;
  completed: boolean;
  completionStatus?: 'completed-after-mistake' | 'answer-shown' | 'incomplete';
};

export type LearningReviewItem = {
  label: string;
  prompt: LearningReviewContent;
  wrongAnswers: LearningReviewContent[];
  correctAnswer: LearningReviewContent;
  notes: string[];
};

function uniqueValidIndices(indices: number[], choiceCount: number): number[] {
  return [...new Set(indices)].filter((index) => Number.isInteger(index) && index >= 0 && index < choiceCount);
}

export function buildLearningReview(
  questions: LearningReviewQuestion[],
  attempts: LearningReviewAttempt[],
): LearningReviewItem[] {
  return attempts.flatMap((attempt) => {
    const question = questions[attempt.questionIndex];
    if (!question) return [];
    const wrongCount = Math.max(0, attempt.wrongCount ?? 0);
    const timeoutCount = Math.max(0, attempt.timeoutCount ?? 0);
    const wrongIndices = uniqueValidIndices(attempt.wrongChoiceIndices ?? [], question.choices.length)
      .filter((index) => !question.choices[index].correct);
    const hasMistake = wrongIndices.length > 0 || wrongCount > 0 || timeoutCount > 0 || !attempt.completed
      || attempt.completionStatus === 'answer-shown' || attempt.completionStatus === 'incomplete';
    if (!hasMistake) return [];

    const correctAnswer = question.choices.find((choice) => choice.correct);
    if (!correctAnswer) return [];
    const notes: string[] = [];
    if (timeoutCount > 0) notes.push(timeoutCount === 1 ? 'Süre doldu; cevap verilmedi.' : `Süre ${timeoutCount} kez doldu.`);
    if (attempt.completionStatus === 'answer-shown') notes.push('Doğru cevap gösterilerek sonraki soruya geçildi.');
    else if (attempt.completionStatus === 'incomplete' || !attempt.completed) notes.push('Bu soru tamamlanamadı.');
    else if (attempt.completionStatus === 'completed-after-mistake' || wrongIndices.length > 0 || wrongCount > 0 || timeoutCount > 0) notes.push('Daha sonra doğru tamamlandı.');
    if (wrongCount > wrongIndices.length) notes.push(`${wrongCount} yanlış deneme yapıldı.`);

    return [{
      label: attempt.label,
      prompt: { text: question.prompt, mediaUrl: question.promptMediaUrl },
      wrongAnswers: wrongIndices.map((index) => question.choices[index]),
      correctAnswer,
      notes,
    }];
  });
}
